/**************************************************************************************************************************
 Copyright (c) 2024 WorkEarly Results LLP

     Name: public.layout.tsx
   Author: Harikrishnan Gangadharan
 Comments: 

/**************************************************************************************************************************
 IMPORTS
***************************************************************************************************************************/
import { useState, type FunctionComponent } from 'react';
import { Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import Logo from '@/shared/logo';
import Statics from '@/constants/statics';
import BurgerButton from '@/shared/burger-button';
/**************************************************************************************************************************
 IMPLEMENTATIONS
***************************************************************************************************************************/
const Public: FunctionComponent = () => {
  const { t } = useTranslation('common');

  const [openMenu, setOpenMenu] = useState<boolean>(false);

  const toggleMenu = () => setOpenMenu(!openMenu);

  return (
    <div className="w-full h-[100vh] bg-gray-100">
      <motion.header
        className="fixed left-0 w-full bg-black/80 shadow-sm lg:shadow-none drop-shadow-sm lg:drop-shadow-none z-1000"
        initial={{ top: '-40%' }}
        animate={{ top: 0 }}
        exit={{ top: '-40%' }}
        transition={{
          ease: 'easeInOut',
          duration: 1
        }}
      >
        <nav
          className={`relative max-w-screen-xl mx-auto w-full min-h-[60px] flex items-center justify-center lg:justify-between px-0 py-4 ${
            !openMenu
              ? 'rounded-br-[20px] rounded-bl-[20px]'
              : 'lg:rounded-br-[20px] lg:rounded-bl-[20px]'
          }`}
        >
          <Logo logoSize={60} titleSize={25} tagLineSize={13}/>
          <BurgerButton open={openMenu} toggleMenu={toggleMenu} />
          <div
            className={`w-full sm:w-[350px] lg:w-fit min-h-[350px] fixed top-[97.8px] lg:top-0 ${
              openMenu ? 'left-0' : 'left-[-100%] lg:left-0'
            } lg:relative w-fit lg:min-h-[40px] bg-theme-900 lg:bg-transparent flex items-center justify-start lg:justify-end transition-all duration-500 shadow-lg lg:shadow-none drop-shadow-lg lg:drop-shadow-none z-10 rounded-tr-[10px] rounded-br-[10px] rounded-bl-[10px] px-5 py-2`}
          >
            <ul className="w-full h-full flex flex-col lg:flex-row items-center justify-start lg:justify-center lg:gap-5">
              {Statics.NavigationLinks.map(({ id, icon: Icon, label, href }) => (
                <a
                  key={id}
                  className="group w-full lg:w-fit min-h-[40px] flex items-center justify-start gap-3 lg:gap-0 border-b-[2px] border-b-transparent lg:hover:border-b-theme-900 transition-all duration-350 border-b-[0.5px] border-b-black/50 px-5 lg:px-2 py-2 lg:py-0"
                  href={href}
                >
                  <div className="w-[40px] h-[40px] bg-black lg:bg-transparent flex items-center justify-center rounded-[8px]">
                    <Icon className="text-xl text-theme-900 lg:text-theme-900/80 lg:group-hover:text-theme-900 transition-all duration-350" />
                  </div>
                  <p className="text-xl lg:text-sm text-black/60 lg:text-white/80 group-hover:text-black lg:group-hover:text-white font-inter font-medium uppercase flex items-center justify-center transition-all duration-350">
                    {t(label)}
                  </p>
                </a>
              ))}
            </ul>
          </div>
        </nav>
      </motion.header>
      <main className="w-full h-[100vh]">
        <div className="w-full h-[97.8px] bg-black"></div>
        <Outlet />
      </main>
    </div>
  );
};

const Layouts = {
  Public
};

export default Layouts;
